import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { localizationService } from '@shared-services/localization-service';
import { useBookingContext } from '../../Context';
import { cancelBooking, getBooking } from '../../api';
import { logBookingError } from '../../loggingUtils';
import { Booking } from '../../types';
import { isBookingValid } from '../../utils';
import XIcon from '../icons/XIcon';
import { BookingDataView, BookingDataViewType } from './BookingDataView';

interface BookingCancelationProps {
    bookingUid: string;
    closeCancelation: () => void;
}

export const BookingCancelation = ({
    bookingUid,
    closeCancelation,
}: BookingCancelationProps) => {
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [cancelBookingData, setCancelBookingData] = useState<Booking | null>(
        null
    );
    const [cancelationResult, setCancelationResult] = useState<Booking | null>(
        null
    );
    const bookerContext = useBookingContext();

    useEffect(() => {
        getBooking(bookingUid)
            .then((bookingData) => {
                if (isBookingValid(bookingData.status)) {
                    setCancelBookingData(bookingData);
                } else {
                    closeCancelation();
                }
            })
            .catch((err) => {
                logBookingError(
                    'Failed to get booking data (cancellation flow)',
                    {
                        errorMessage: err.message,
                        additionalInfoToLog: bookerContext,
                    }
                );
            });
    }, []);

    const cancel = useCallback(
        (cancellationReason: string) => {
            setIsLoading(true);
            if (bookingUid) {
                setIsLoading(true);
                cancelBooking(bookingUid, cancellationReason)
                    .then(setCancelationResult)
                    .catch((err) => {
                        logBookingError('Failed to cancel booking', {
                            errorMessage: err.message,
                            additionalInfoToLog: bookerContext,
                        });
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        },
        [bookingUid]
    );

    if (!cancelBookingData) {
        return null;
    }

    if (cancelationResult) {
        return (
            <BookingDataView
                bookingData={cancelBookingData}
                title={localizationService.str(
                    'ui.runtimessr.calbooking.canceledBooking.title'
                )}
                svgIcon={<XIcon />}
                type={BookingDataViewType.CANCELATION}
                onClose={closeCancelation}
            />
        );
    }
    return (
        <BookingDataView
            bookingData={cancelBookingData}
            title={localizationService.str(
                'ui.runtimessr.calbooking.cancelBooking.title'
            )}
        >
            <Footer>
                <Label>
                    {localizationService.str(
                        'ui.runtimessr.calbooking.cancelBooking.reason'
                    )}
                </Label>
                <TextArea
                    value={reason}
                    name='reason'
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={localizationService.str(
                        'ui.runtimessr.calbooking.cancelBooking.cancelling.placeholder'
                    )}
                />
                <StyledButton
                    onClick={() => cancel(reason)}
                    disabled={isLoading}
                >
                    {isLoading
                        ? localizationService.str(
                              'ui.runtimessr.calbooking.cancelBooking.cancelling'
                          )
                        : localizationService.str(
                              'ui.runtimessr.calbooking.cancelBooking.cancelButton'
                          )}
                </StyledButton>
            </Footer>
        </BookingDataView>
    );
};

const Label = styled.label`
    text-align: start;
    margin-block-end: 8px;
    font-weight: 350;
`;

const TextArea = styled.textarea`
    && {
        background-color: transparent;
        border: 1px solid #93959a;
        border-radius: 4px;
        margin-block-start: 8px;
        height: 100px;
        padding: 8px;
        box-sizing: border-box;
        resize: vertical;
        &::placeholder {
            color: #93959a;
            font-size: 13px;
        }
    }
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const StyledButton = styled.button`
    height: 32px;
    width: fit-content;
    background-color: #000;
    color: #fff;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 550;
    margin-inline-start: auto;
    padding-inline: 16px;
    margin-block-start: 16px;
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;
